import { AccordionSummary, accordionSummaryClasses, Stack, Typography, useTheme } from "@mui/material";
import { addDays, format } from "date-fns";
import { Dispatch, SetStateAction } from "react";
import { HomepageDownloadSummary, HomepageSparklineDay, HomepageUploadSummary } from "../../../graphql/types";

import { CaretDownIcon } from "@phosphor-icons/react/dist/csr/CaretDown";
import { CloudArrowDownIcon } from "@phosphor-icons/react/dist/csr/CloudArrowDown";
import { CloudArrowUpIcon } from "@phosphor-icons/react/dist/csr/CloudArrowUp";

import { GroupingAccordion } from "./GroupingAccordion";
import { NoItems, SparklineSquare } from "./Rows";
import { InfoProject } from "./types";
import { SparklineStatus } from "./utils";

const statusOrdinal = (status: SparklineStatus): number => {
  switch (status) {
    case SparklineStatus.Clean:
      return 0;
    case SparklineStatus.Expected:
      return 1;
    case SparklineStatus.Missing:
      return 2;
    case SparklineStatus.Warning:
      return 3;
    case SparklineStatus.Issue:
      return 4;
    case SparklineStatus.Failed:
      return 5;
    default:
      return Number.MAX_SAFE_INTEGER;
  }
};

const SevenDaysRollupSparkline = ({
  items,
  sparklineFirstDate,
}: {
  items: { sparkline: Array<HomepageSparklineDay> }[];
  sparklineFirstDate: string;
}) => {
  const [year, month, day] = sparklineFirstDate.split("-");
  const sparklineFirstDateDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day), 0, 0, 0, 0);
  const sparklineGlobalStatus: Array<{
    status: SparklineStatus;
    running: boolean;
  }> = [];

  for (let i = 0; i < 7; i++) {
    sparklineGlobalStatus.push({
      status: SparklineStatus.Clean,
      running: false,
    });
  }

  items.forEach((item) => {
    item.sparkline.forEach((day, index) => {
      if (statusOrdinal(day.status as SparklineStatus) >= statusOrdinal(sparklineGlobalStatus[index].status)) {
        sparklineGlobalStatus[index].status = day.status as SparklineStatus;
        sparklineGlobalStatus[index].running = sparklineGlobalStatus[index].running || day.running;
      }
    });
  });

  return (
    <Stack direction="row" sx={{ width: "100%", gap: "4px", alignItems: "center" }}>
      {sparklineGlobalStatus.map((sparkline, index) => (
        <SparklineSquare key={index} sparkline={sparkline} day={format(addDays(sparklineFirstDateDate, index), "d")} />
      ))}
    </Stack>
  );
};

const AccordionSummaryProject = ({
  name,
  downloads,
  uploads,
  sparklineFirstDate,
}: {
  name: string;
  downloads: Omit<HomepageDownloadSummary, "status" | "keys">[];
  uploads: Omit<HomepageUploadSummary, "status" | "keys" | "dependencies">[];
  sparklineFirstDate: string;
}) => {
  const theme = useTheme();
  return (
    <AccordionSummary
      expandIcon={<CaretDownIcon size="24px" color={theme.palette.text50.main} />}
      aria-controls="panel1-content"
      id="panel1-header"
      sx={{
        backgroundColor: theme.palette.surfaceLight20.main,
        padding: "16px",
        height: "56px",
        borderRadius: "8px",
        [`&.${accordionSummaryClasses.expanded}`]: {
          borderRadius: "8px 8px 0 0",
          borderBottom: `1px solid ${theme.palette.surfaceLight50.main}`,
          borderTop: `1px solid transparent`,
          boxSizing: "border-box",
        },
      }}
    >
      <Stack sx={{ display: "grid", gridTemplateColumns: "1fr 114px 270px", width: "100%" }}>
        <Stack sx={{ width: "100%", overflow: "hidden" }}>
          <Typography
            variant="heading.seven"
            sx={{
              color: "text50.main",
              marginLeft: "60px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {name}
          </Typography>
        </Stack>
        <Stack direction="row" sx={{ gap: "16px", alignItems: "center", justifySelf: "flex-end" }}>
          {downloads?.length > 0 && (
            <Stack direction="row" sx={{ gap: "4px" }}>
              <Typography variant="body.medium.semibold" sx={{ color: "text40.main" }}>
                {downloads?.length}
              </Typography>
              <CloudArrowDownIcon color={theme.palette.text40.main} size="24px" />
            </Stack>
          )}
          {uploads?.length > 0 && (
            <Stack direction="row" sx={{ gap: "4px" }}>
              <Typography variant="body.medium.semibold" sx={{ color: "text40.main" }}>
                {uploads?.length}
              </Typography>
              <CloudArrowUpIcon color={theme.palette.text40.main} size="24px" />
            </Stack>
          )}
        </Stack>
        <Stack sx={{ justifySelf: "flex-end", mr: "52px" }}>
          <SevenDaysRollupSparkline
            items={[...(downloads || []), ...(uploads || [])]}
            sparklineFirstDate={sparklineFirstDate}
          />
        </Stack>
      </Stack>
    </AccordionSummary>
  );
};

type ProjectsProps = {
  infoProject: InfoProject;
  sparklineFirstDate: string;
  expandedItems: string[];
  setExpandedItems: Dispatch<SetStateAction<string[]>>;
  refreshData: () => Promise<void>;
  refreshing: boolean;
};

const Projects = ({
  infoProject,
  sparklineFirstDate,
  expandedItems,
  setExpandedItems,
  refreshData,
  refreshing,
}: ProjectsProps) => {
  if (Object.keys(infoProject).length === 0) {
    return <NoItems />;
  }
  const sortedInfoProject = Object.keys(infoProject).sort();
  return (
    <Stack sx={{ gap: "16px" }}>
      {sortedInfoProject.map((key, index) => {
        const sortedDownloads = infoProject[key].downloads?.sort((a, b) => {
          return a.name.localeCompare(b.name);
        });
        const sortedUploads = infoProject[key].uploads?.sort((a, b) => {
          return a.name.localeCompare(b.name);
        });
        return (
          <GroupingAccordion
            refreshData={refreshData}
            index={index}
            key={key}
            downloads={sortedDownloads ?? []}
            uploads={sortedUploads ?? []}
            sparklineFirstDate={sparklineFirstDate}
            expanded={expandedItems.includes(key)}
            setExpanded={(expanded: boolean) => {
              setExpandedItems((prevState) =>
                expanded ? [...prevState, key] : prevState.filter((project) => project !== key)
              );
            }}
            refreshing={refreshing}
          >
            <AccordionSummaryProject
              name={key}
              downloads={sortedDownloads}
              uploads={sortedUploads}
              sparklineFirstDate={sparklineFirstDate}
            />
          </GroupingAccordion>
        );
      })}
    </Stack>
  );
};

export { Projects };
